import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Upload, X } from 'lucide-react';
import type { ChangeEvent, ReactNode } from 'react';
import type { CsvUploadResult } from './model';
import { splitCsvRows } from './model';

interface CsvBulkUploadDialogProps {
  title: string;
  description: string;
  destination: string;
  csvContent: string;
  fileName: string;
  result: CsvUploadResult | null;
  isUploading: boolean;
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
  onUpload: () => void;
  onClose: () => void;
  instructions: ReactNode;
  selector?: ReactNode;
  onDownloadTemplate?: () => void;
  uploadLabel?: string;
  maxWidth?: 'lg' | '2xl';
  uploadDisabled?: boolean;
  fileLabel?: string;
  compactResult?: boolean;
  doneLabel?: string;
  showUploadRowCount?: boolean;
  previewMode?: 'rows' | 'count';
  truncatePreviewLines?: boolean;
  secondaryActionFirst?: boolean;
  doneVariant?: 'default' | 'outline';
  maxHeight?: '80vh' | '90vh';
}

export function CsvBulkUploadDialog({
  title,
  description,
  destination,
  csvContent,
  fileName,
  result,
  isUploading,
  onFileSelect,
  onUpload,
  onClose,
  instructions,
  selector,
  onDownloadTemplate,
  uploadLabel = 'Upload',
  maxWidth = '2xl',
  uploadDisabled = false,
  fileLabel = 'Select CSV File',
  compactResult = false,
  doneLabel = 'Done',
  showUploadRowCount = true,
  previewMode = 'rows',
  truncatePreviewLines = true,
  secondaryActionFirst = false,
  doneVariant = 'default',
  maxHeight = '90vh',
}: CsvBulkUploadDialogProps) {
  const csvRows = splitCsvRows(csvContent);
  const dataRowCount = Math.max(0, csvRows.length - 1);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4">
      <Card
        className={`w-full ${maxWidth === 'lg' ? 'max-w-lg' : 'max-w-2xl'} mx-4 animate-fade-in ${maxHeight === '80vh' ? 'max-h-[80vh]' : 'max-h-[90vh]'} overflow-y-auto`}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-primary" />
              {title}
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardDescription>{description}</CardDescription>
          <div className="flex items-center gap-2 mt-2 p-2 rounded-md bg-primary/10 border border-primary/20">
            <span className="text-xs text-muted-foreground">Uploading to:</span>
            <Badge className="bg-primary text-primary-foreground text-xs">{destination}</Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {selector}

          {onDownloadTemplate && (
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <div>
                <p className="text-sm font-medium">CSV Template</p>
                <p className="text-xs text-muted-foreground">
                  Download a template with the correct column headers
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={onDownloadTemplate}>
                Download Template
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1 p-3 rounded-lg border border-border">
            {instructions}
          </div>

          <div className="space-y-2">
            <Label htmlFor="csv-bulk-upload-file">{fileLabel}</Label>
            <Input
              id="csv-bulk-upload-file"
              type="file"
              accept=".csv"
              onChange={onFileSelect}
              className="cursor-pointer"
            />
            {fileName && <p className="text-sm text-muted-foreground">Selected: {fileName}</p>}
          </div>

          {csvContent && !result && previewMode === 'count' && (
            <Badge variant="secondary">{dataRowCount} data rows detected</Badge>
          )}

          {csvContent && !result && previewMode === 'rows' && (
            <div className="space-y-2">
              <Label>Preview ({dataRowCount} rows)</Label>
              <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-3 bg-muted/30 text-xs font-mono">
                {csvRows.slice(0, 6).map((line, index) => (
                  <div
                    key={`preview-line-${line.slice(0, 40)}-${index}`}
                    className={index === 0 ? 'font-bold text-primary' : 'text-muted-foreground'}
                  >
                    {truncatePreviewLines && line.length > 120 ? `${line.slice(0, 120)}…` : line}
                  </div>
                ))}
                {csvRows.length > 6 && (
                  <div className="text-muted-foreground mt-1">
                    ... and {csvRows.length - 6} more rows
                  </div>
                )}
              </div>
            </div>
          )}

          {result && compactResult && (
            <div className="space-y-2 rounded-lg border p-3">
              <p className="text-sm font-medium">{result.message}</p>
              {result.row_errors.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {result.row_errors.map((error) => (
                    <p key={`row-${error.row}-${error.error}`} className="text-xs text-destructive">
                      Row {error.row}: {error.error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {result && !compactResult && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="text-2xl font-bold">{result.total_rows}</p>
                  <p className="text-xs text-muted-foreground">Total Rows</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10 text-center">
                  <p className="text-2xl font-bold text-green-500">{result.created}</p>
                  <p className="text-xs text-green-500">Created</p>
                </div>
                <div className="p-3 rounded-lg bg-red-500/10 text-center">
                  <p className="text-2xl font-bold text-red-500">{result.errors}</p>
                  <p className="text-xs text-red-500">Errors</p>
                </div>
              </div>

              {result.row_errors.length > 0 && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-red-500">Errors:</p>
                  <div className="max-h-32 overflow-y-auto rounded-lg border border-red-500/20 p-2 bg-red-500/5 text-xs">
                    {result.row_errors.map((error) => (
                      <p key={`row-${error.row}-${error.error}`} className="text-red-400">
                        Row {error.row}: {error.error}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            {!result ? (
              secondaryActionFirst ? (
                <>
                  <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    className="flex-1"
                    onClick={onUpload}
                    disabled={!csvContent || isUploading || uploadDisabled}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadLabel}{' '}
                        {showUploadRowCount && csvContent ? `(${dataRowCount} rows)` : ''}
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    className="flex-1"
                    onClick={onUpload}
                    disabled={!csvContent || isUploading || uploadDisabled}
                  >
                    {isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadLabel}{' '}
                        {showUploadRowCount && csvContent ? `(${dataRowCount} rows)` : ''}
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                </>
              )
            ) : (
              <Button className="flex-1" variant={doneVariant} onClick={onClose}>
                {doneLabel}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
